import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { createUseFetchSearchFn } from '@common/search.services';
import { createUseFetchOptionsWithSearchFn } from '@common/selectOptions.services';
import { IApiBaseProps, IUseFetchOptionsParams } from '@common/api.models';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isValidApiBaseProps } from '@common/api.services';
import { AxiosErrorResponse } from '@core/error.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import {
  getMasterScheduleOptionsForDesignBriefAsync,
  getMasterScheduleOptionsForDesignModificationAsync,
  getMasterScheduleOptionsForLoadingRemovalCertAsync,
  getMasterScheduleOptionsForTransmittalAsync,
  getMasterScheduleOptionsWithSearchAsync,
  getMasterScheduleTwcOptionsForLoadingRemovalCertAsync,
  getProjectMasterScheduleOptionsForAssignT1TeamsAsync,
  IGetMasterScheduleOptionsForTransmittalAsyncProps,
  postProjectMasterSchedulesBatchAdd,
  searchMasterScheduleAsync,
  upsertProjectTeamMasterSchedulesAsync,
} from './masterSchedule.services';
import {
  IMasterScheduleSearchFilter,
  IMasterScheduleSearchResult,
  IMasterScheduleTwcOptionsForLoadingRemovalCertRequest,
  IProjectMasterScheduleOptionWithProjectTeamMasterScheduleRequest,
  IProjectMasterScheduleOptionWithRoles,
  IProjectMasterScheduleTwcOption,
  IProjectTeamMasterScheduleAssignProjectMasterScheduleOption,
  IUpsertProjectTeamMasterScheduleRequest,
} from './masterSchedule.models';
import { IMasterScheduleDetail } from './detail/masterScheduleDetail.models';
import { ICreateBatchProjectMasterScheduleRequest } from './masterScheduleBatchAdd.models';

export const useFetchMasterSchedule = createUseFetchSearchFn<IMasterScheduleSearchFilter, IMasterScheduleSearchResult>({
  queryName: FETCH_QUERIES_NAME.MASTER_SCHEDULE_SEARCH,
  searchAsyncFn: searchMasterScheduleAsync,
});

export const useFetchMasterScheduleOptionsWithSearch = ({
  isDirty = false,
  shouldConvertOptions = false,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsWithSearchFn({
    queryName: FETCH_QUERIES_NAME.MASTER_SCHEDULE_OPTIONS,
    getOptionsWithSearchFn: getMasterScheduleOptionsWithSearchAsync,
    handleOptionsProps: { shouldConvertOptions },
    enabled: !isDirty,
  });

export const useGetMasterScheduleOptionsForTransmittal = (
  { transmittalType, ...props }: IGetMasterScheduleOptionsForTransmittalAsyncProps,
  enabled = true,
) => {
  return useQuery<IProjectMasterScheduleOptionWithRoles[], AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.MASTER_SCHEDULE_OPTIONS_FOR_TRANSMITTAL, transmittalType],
    async () => {
      const result = await getMasterScheduleOptionsForTransmittalAsync({ transmittalType, ...props });
      return result.items || [];
    },
    { enabled: isValidApiBaseProps(props) && !!transmittalType && enabled },
  );
};

export const useGetMasterScheduleOptionsForDesignBrief = (props: IApiBaseProps, enabled = true) => {
  return useQuery<IProjectMasterScheduleOptionWithRoles[], AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.MASTER_SCHEDULE_OPTIONS_FOR_DESIGN_BFIEF],
    async () => {
      const result = await getMasterScheduleOptionsForDesignBriefAsync(props);
      return result.items || [];
    },
    { enabled: isValidApiBaseProps(props) && enabled },
  );
};

export const useGetMasterScheduleOptionsForDesignModification = (props: IApiBaseProps, enabled = true) => {
  return useQuery<IProjectMasterScheduleOptionWithRoles[], AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.MASTER_SCHEDULE_OPTIONS_FOR_DESIGN_MODIFICATION],
    async () => {
      const result = await getMasterScheduleOptionsForDesignModificationAsync(props);
      return result.items || [];
    },
    { enabled: isValidApiBaseProps(props) && enabled },
  );
};

export const useGetMasterScheduleOptionsForLoadingRemovalCert = (props: IApiBaseProps, enabled = true) => {
  return useQuery<IProjectMasterScheduleOptionWithRoles[], AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.MASTER_SCHEDULE_OPTIONS_FOR_LOADING_REMOVAL_CERT],
    async () => {
      const result = await getMasterScheduleOptionsForLoadingRemovalCertAsync(props);
      return result.items || [];
    },
    { enabled: isValidApiBaseProps(props) && enabled },
  );
};

export const useFetchMasterScheduleTwcOptionsForLoadingRemovalCert = (
  props: IMasterScheduleTwcOptionsForLoadingRemovalCertRequest,
  enabled = true,
) => {
  return useQuery<IProjectMasterScheduleTwcOption[], AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.MASTER_SCHEDULE_TWC_OPTIONS_FOR_LOADING_REMOVAL_CERT],
    async () => {
      const result = await getMasterScheduleTwcOptionsForLoadingRemovalCertAsync(props);
      return result.items || [];
    },
    { enabled: isValidApiBaseProps(props) && isPositiveNumber(props.masterScheduleId) && enabled },
  );
};

export const useGetProjectMasterScheduleOptionsForAssignT1TeamsAsync = (
  props: IProjectMasterScheduleOptionWithProjectTeamMasterScheduleRequest,
  enabled = true,
) => {
  return useQuery<IProjectTeamMasterScheduleAssignProjectMasterScheduleOption, AxiosErrorResponse>(
    [props.projectId, props.projectCompanyId, FETCH_QUERIES_NAME.MASTER_SCHEDULE_OPTIONS_FOR_ASSIGN_T1_TEAMS],
    async () => {
      const result = await getProjectMasterScheduleOptionsForAssignT1TeamsAsync(props);
      return result;
    },
    { enabled: isValidApiBaseProps(props) && enabled },
  );
};

export const useUpsertProjectTeamMasterSchedulesAsync = () => {
  return useMutation<null, AxiosErrorResponse, IUpsertProjectTeamMasterScheduleRequest[], unknown>(
    [MUTATION_KEYS.UPSERT_PROJECT_TEAM_MASTER_SCHEDULE],
    async (upsertRequest: IUpsertProjectTeamMasterScheduleRequest[]) => {
      const result = await upsertProjectTeamMasterSchedulesAsync(upsertRequest);
      return result;
    },
  );
};

export const useBatchAddProjectMasterSchedules = () => {
  return useMutation<IMasterScheduleDetail[], AxiosErrorResponse, ICreateBatchProjectMasterScheduleRequest>(
    [MUTATION_KEYS.BATCH_ADD_MASTER_SCHEDULE],
    async (request: ICreateBatchProjectMasterScheduleRequest) => {
      const result = await postProjectMasterSchedulesBatchAdd(request);
      return result;
    },
  );
};
